/* eslint-disable camelcase */
import { AppUiHelper } from '../../../../helpers/AppUiHelper'
import { buildPreviewComponentsFromTemplate } from '../WhatsappPreviewAdapter'

function upper(value) {
  return String(value || '').trim().toUpperCase()
}

function lower(value) {
  return String(value || '').trim().toLowerCase()
}

function placeholderCount(text) {
  const re = /\{\{(\d+)\}\}/g
  let max = 0
  let m
  const s = String(text || '')
  while ((m = re.exec(s))) {
    const n = parseInt(m[1], 10)
    if (n > max) max = n
  }
  return max
}

function slotKey(slot) {
  return [upper(slot && slot.type), lower(slot && slot.format), String((slot && slot.vIndex) || ''), String((slot && slot.buttonId) || '')].join('|')
}

export function slotsFromStructure(structure) {
  const slots = []
  let nextId = 1
  const comps = Array.isArray(structure) ? structure : []
  for (let c = 0; c < comps.length; c++) {
    const comp = comps[c]
    if (!comp || typeof comp !== 'object') continue
    const type = upper(comp.type)
    if (type === 'HEADER') {
      const fmt = upper(comp.format)
      if (fmt === 'TEXT') {
        const n = placeholderCount(comp.text)
        for (let i = 1; i <= n; i++) {
          slots.push({ id: String(nextId++), type: 'HEADER', format: 'text', vIndex: i, text: '' })
        }
      } else if (fmt === 'IMAGE' || fmt === 'VIDEO' || fmt === 'DOCUMENT') {
        slots.push({
          id: String(nextId++),
          type: 'HEADER',
          format: lower(fmt),
          media: '',
          mediaLink: '',
          fileName: '',
        })
      }
    } else if (type === 'BODY') {
      const n = placeholderCount(comp.text)
      for (let i = 1; i <= n; i++) {
        slots.push({ id: String(nextId++), type: 'BODY', format: 'text', vIndex: i, text: '' })
      }
    } else if (type === 'BUTTONS') {
      const buttons = Array.isArray(comp.buttons) ? comp.buttons : []
      for (let i = 0; i < buttons.length; i++) {
        const b = buttons[i]
        if (!b || upper(b.type) !== 'URL') continue
        if (!/\{\{\d+\}\}/.test(String(b.url || ''))) continue
        slots.push({
          id: String(nextId++),
          type: 'URL',
          format: 'text',
          vIndex: 1,
          buttonId: String(i),
          text: '',
        })
      }
    }
  }
  return slots
}

function slotLabel(slot) {
  const t = upper(slot && slot.type)
  const f = lower(slot && slot.format)
  if (t === 'HEADER' && f === 'text') return 'Header {{' + (slot.vIndex || 1) + '}}'
  if (t === 'HEADER' && f === 'image') return 'Header image'
  if (t === 'HEADER' && f === 'video') return 'Header video'
  if (t === 'HEADER' && f === 'document') return 'Header document'
  if (t === 'BODY') return 'Body {{' + (slot.vIndex || 1) + '}}'
  if (t === 'URL') return 'URL button'
  return 'Variable'
}

export function slotFieldKey(slotId, suffix) {
  return 'slot_' + String(slotId) + '_' + suffix
}

function contactVariableValues(crmContactsFieldDef) {
  return AppUiHelper.getContactTemplateVariables(crmContactsFieldDef).map(ctv => ctv.value)
}

function textFieldsForSlot(slot, crmContactsFieldDef, placement) {
  const textKey = slotFieldKey(slot.id, 'text')
  const customKey = slotFieldKey(slot.id, 'customText')
  const options = AppUiHelper.getContactTemplateVariables(crmContactsFieldDef)
    .map(ctv => ({ key: ctv.value, label: ctv.text }))
    .concat({ key: 'custom', label: '[Custom Text]' })
  return [
    {
      key: textKey,
      type: 'radio',
      label: slotLabel(slot),
      tip: 'Choose one of the contact variables (or enter a custom text)',
      options,
      v_required: {
        value: true,
        err: 'Choose one of the contact variables (or enter a custom text)',
      },
      placement,
    },
    {
      condition: { key: textKey, value: 'custom', values: ['custom'] },
      key: customKey,
      type: 'edit_text',
      label: 'Custom Value',
      tip: 'Enter any text',
      v_required: {
        value: true,
        err: 'Provide custom value',
      },
      placement,
    },
  ]
}

function mediaFieldsForSlot(slot, placement) {
  const format = lower(slot && slot.format)
  const mediaKey = slotFieldKey(slot.id, 'media')
  const linkName = slotFieldKey(slot.id, 'mediaLink')
  if (format === 'image') {
    return [{
      key: mediaKey,
      linkName,
      type: 'edit_text',
      label: slotLabel(slot) + ' (PNG/JPEG, max 1920px height)',
      imageHeight: 1920,
      mode: 'fit',
      tip: 'Upload an image in PNG or JPEG format',
      v_required: { value: true, err: 'Provide image' },
      v_min: { value: 1, err: 'Invalid image' },
      v_max: { value: 100, err: 'Invalid image' },
      imageUpload: true,
      uploadType: 'media',
      uploadAccept: 'image/png, image/jpeg',
      placement,
    }]
  }
  if (format === 'video') {
    return [{
      key: mediaKey,
      linkName,
      label: slotLabel(slot) + ' (MP4, max 16MB file size)',
      hint: 'Upload a video in MP4 format',
      v_required: { value: true, err: 'Provide video' },
      placement,
      type: 'edit_text',
      subType: 'VIDEO',
      documentUpload: true,
      v_min: { value: 1, err: 'Invalid video' },
      v_max: { value: 100, err: 'Invalid video' },
      uploadType: 'media',
      uploadAccept: 'video/mp4',
      fileSizeLimit: 16000000,
    }]
  }
  if (format === 'document') {
    return [
      {
        key: mediaKey,
        linkName,
        label: slotLabel(slot),
        hint: 'Upload PDF document (max 20MB)',
        v_required: { value: true, err: 'Provide document' },
        placement: 'left',
        type: 'edit_text',
        documentUpload: true,
        v_min: { value: 1, err: 'Invalid document' },
        v_max: { value: 100, err: 'Invalid document' },
        uploadType: 'media',
        uploadAccept: 'application/pdf',
        fileSizeLimit: 20000000,
      },
      {
        key: slotFieldKey(slot.id, 'fileName'),
        type: 'edit_text',
        label: 'File Name',
        tip: 'Set the document filename to send',
        v_required: { value: true, err: 'Provide file name' },
        placement: 'right',
      },
    ]
  }
  return []
}

function fieldsForSlot(slot, crmContactsFieldDef, placement) {
  if (lower(slot && slot.format) === 'text') return textFieldsForSlot(slot, crmContactsFieldDef, placement)
  return mediaFieldsForSlot(slot, placement)
}

function ensureTemplateOptions(templateOptions, currentId) {
  const options = Array.isArray(templateOptions) ? templateOptions.slice() : []
  const id = String(currentId || '').trim()
  if (!id) return options
  for (let i = 0; i < options.length; i++) {
    if (options[i] && options[i].key === id) return options
  }
  const rest = options.filter(o => o && o.key)
  return [{ key: id, label: id }].concat(rest.length ? rest : [])
}

export function patchTemplateSelectOptions(fieldDef, options, currentId) {
  const fields = fieldDef && fieldDef.steps && fieldDef.steps[0] && fieldDef.steps[0].fields
  if (!Array.isArray(fields)) return false
  for (let i = 0; i < fields.length; i++) {
    if (fields[i] && fields[i].key === 'templateId') {
      fields[i].options = ensureTemplateOptions(options, currentId)
      return true
    }
  }
  return false
}

function mergeSlotValues(baseSlots, savedSlots) {
  const savedByKey = {}
  ;(savedSlots || []).forEach(s => {
    if (s) savedByKey[slotKey(s)] = s
  })
  return (baseSlots || []).map(slot => {
    const saved = savedByKey[slotKey(slot)]
    if (!saved) return slot
    return {
      ...slot,
      text: saved.text || slot.text,
      media: saved.media || slot.media,
      mediaLink: saved.mediaLink || slot.mediaLink,
      fileName: saved.fileName || slot.fileName,
    }
  })
}

function sameTextGroup(a, b) {
  return !!(a && b
    && lower(a.format) === 'text'
    && lower(b.format) === 'text'
    && upper(a.type) === upper(b.type))
}

function buildSlotFields(slots, crmContactsFieldDef) {
  const fields = []
  const list = slots || []
  let i = 0
  while (i < list.length) {
    const slot = list[i]
    const fmt = lower(slot && slot.format)
    if (fmt === 'document') {
      fields.push.apply(fields, mediaFieldsForSlot(slot))
      i += 1
      continue
    }
    if (fmt === 'image' || fmt === 'video') {
      fields.push.apply(fields, mediaFieldsForSlot(slot, 'left'))
      i += 1
      continue
    }
    const next = list[i + 1]
    if (sameTextGroup(slot, next)) {
      fields.push.apply(fields, fieldsForSlot(slot, crmContactsFieldDef, 'left'))
      fields.push.apply(fields, fieldsForSlot(next, crmContactsFieldDef, 'right'))
      i += 2
      continue
    }
    fields.push.apply(fields, fieldsForSlot(slot, crmContactsFieldDef, 'left'))
    i += 1
  }
  return fields
}

function templateIdField(options, extra) {
  const field = {
    key: 'templateId',
    type: 'select',
    label: 'Template',
    tip: 'Approved templates from the default WhatsApp number',
    options,
    v_required: { value: true, err: 'Provide WhatsApp template' },
    placement: 'left',
  }
  if (extra && extra.disabled) field.disabled = true
  return field
}

function categoryField(category) {
  const value = category || ''
  return {
    key: 'templateCategory',
    type: 'edit_text',
    label: 'Category',
    disabled: true,
    placement: 'right',
    v_required: { value: false, err: '' },
    value,
    defaultValue: value,
  }
}

export function getChatbotTemplateSelectFieldDef(templateOptions, nodeData) {
  const currentId = String((nodeData && nodeData.templateId) || '')
  return {
    title: 'Send Template',
    formType: 'edit',
    ctaButtonText: 'Next',
    templateFormStep: 'select',
    steps: [{ fields: [templateIdField(ensureTemplateOptions(templateOptions, currentId))] }],
  }
}

export function getChatbotTemplateFillFieldDef(templateOptions, crmContactsFieldDef, nodeData, extra) {
  const extras = extra || {}
  const templatesByName = extras.templatesByName || {}
  const data = nodeData || {}
  const selectedId = String(extras.selectedTemplateId || data.templateId || '')
  const originalTemplateId = String(data.templateId || '')
  const originalStructure = Array.isArray(data.structure) ? data.structure : []
  const originalComponents = Array.isArray(data.components) ? data.components : []
  const tpl = templatesByName[selectedId]
  let structure = []
  if (tpl && Array.isArray(tpl.components)) structure = tpl.components
  else if (selectedId && selectedId === originalTemplateId) structure = originalStructure
  const baseSlots = slotsFromStructure(structure)
  const slots = (selectedId && selectedId === originalTemplateId && originalComponents.length)
    ? mergeSlotValues(baseSlots, originalComponents)
    : baseSlots
  return {
    title: 'Send Template',
    formType: 'edit',
    ctaButtonText: 'Save',
    preview: {
      component: 'chatbot_template_preview_adapter',
      title: 'Preview',
      paddingBottom: 48,
    },
    templatesByName,
    templateStructure: structure,
    templateSlots: slots,
    steps: [{
      fields: [
        templateIdField(ensureTemplateOptions(templateOptions, selectedId), { disabled: true }),
        categoryField(upper(tpl && tpl.category)),
      ].concat(buildSlotFields(slots, crmContactsFieldDef)),
    }],
  }
}

export function templateNodeDataToFormItem(data, crmContactsFieldDef) {
  const d = data || {}
  const item = { templateId: d.templateId || '', templateCategory: upper(d.category) }
  const known = contactVariableValues(crmContactsFieldDef)
  const slots = Array.isArray(d.components) ? d.components : []
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i] || {}
    const id = slot.id
    if (!id) continue
    if (lower(slot.format) === 'text') {
      const text = String(slot.text || '').trim()
      if (text && known.indexOf(text) === -1) {
        item[slotFieldKey(id, 'text')] = 'custom'
        item[slotFieldKey(id, 'customText')] = text
      } else {
        item[slotFieldKey(id, 'text')] = text
        item[slotFieldKey(id, 'customText')] = ''
      }
    } else {
      item[slotFieldKey(id, 'media')] = slot.media || ''
      item[slotFieldKey(id, 'mediaLink')] = slot.mediaLink || ''
      item[slotFieldKey(id, 'fileName')] = slot.fileName || ''
    }
  }
  return item
}

function applyCallerMedia(formData, callerState) {
  const fd = { ...(formData || {}) }
  const st = callerState || {}
  Object.keys(st).forEach(k => {
    if (k.indexOf('slot_') === 0 && st[k]) fd[k] = st[k]
  })
  return fd
}

function readSlotFromForm(fd, slot) {
  const id = slot.id
  const next = {
    id,
    type: slot.type,
    format: slot.format,
  }
  if (slot.vIndex) next.vIndex = slot.vIndex
  if (slot.buttonId != null && slot.buttonId !== '') next.buttonId = slot.buttonId
  if (lower(slot.format) === 'text') {
    const chosen = String(fd[slotFieldKey(id, 'text')] || '').trim()
    next.text = chosen === 'custom' ? String(fd[slotFieldKey(id, 'customText')] || '').trim() : chosen
    return next
  }
  next.media = String(fd[slotFieldKey(id, 'media')] || '').trim()
  next.mediaLink = String(fd[slotFieldKey(id, 'mediaLink')] || '').trim()
  if (lower(slot.format) === 'document') {
    next.fileName = String(fd[slotFieldKey(id, 'fileName')] || '').trim()
  }
  return next
}

export function formDataToTemplateNodeData(formData, prevData, options) {
  const opts = options || {}
  const fd = applyCallerMedia(formData, opts.callerState)
  const prev = prevData || {}
  const templatesByName = opts.templatesByName || {}
  const templateId = String(fd.templateId || '').trim()
  const tpl = templatesByName[templateId]
  const sameTemplate = templateId === String(prev.templateId || '').trim()
  let structure = []
  let language = ''
  if (tpl) {
    structure = Array.isArray(tpl.components) ? tpl.components : []
    language = tpl.language || prev.language || ''
  } else if (sameTemplate) {
    structure = Array.isArray(prev.structure) ? prev.structure : []
    language = prev.language || ''
  }
  const baseSlots = slotsFromStructure(structure)
  const prevByKey = {}
  if (sameTemplate) {
    (Array.isArray(prev.components) ? prev.components : []).forEach(s => {
      if (s) prevByKey[slotKey(s)] = s
    })
  }
  const components = baseSlots.map(slot => {
    const fromForm = readSlotFromForm(fd, slot)
    const hasText = lower(slot.format) === 'text' && fromForm.text
    const hasMedia = lower(slot.format) !== 'text' && (fromForm.media || fromForm.mediaLink)
    if (hasText || hasMedia || fromForm.fileName) return fromForm
    const kept = prevByKey[slotKey(slot)]
    if (!kept) return fromForm
    return {
      ...fromForm,
      text: kept.text || fromForm.text,
      media: kept.media || fromForm.media,
      mediaLink: kept.mediaLink || fromForm.mediaLink,
      fileName: kept.fileName || fromForm.fileName,
    }
  })
  return {
    wabaNumber: prev.wabaNumber || opts.defaultWaba || '',
    templateId,
    language,
    structure,
    components,
  }
}

export function previewSlotsFromFormData(formData, slots) {
  const fd = formData || {}
  return (slots || []).map(slot => readSlotFromForm(fd, slot))
}

function exampleAt(arr, vIndex) {
  const idx = (vIndex || 1) - 1
  if (!Array.isArray(arr) || idx < 0) return ''
  return arr[idx] != null ? String(arr[idx]) : ''
}

function replaceVars(text, examples) {
  return String(text || '').replace(/\{\{(\d+)\}\}/g, (m, n) => {
    const v = exampleAt(examples, parseInt(n, 10))
    return v.trim() ? v : m
  })
}

export function templateNodeToBubbleData(data) {
  const d = data || {}
  const structure = (d.waTemplate && Array.isArray(d.waTemplate.components))
    ? d.waTemplate.components
    : (Array.isArray(d.structure) ? d.structure : [])
  const slots = (d.waComponents && typeof d.waComponents === 'object' && !Array.isArray(d.waComponents))
    ? d.waComponents
    : (Array.isArray(d.components) ? d.components : [])
  const slotList = Array.isArray(slots)
    ? slots
    : Object.keys(slots || {}).map(k => slots[k])
  const components = buildPreviewComponentsFromTemplate(structure, slots, { hideTemplateExamplesWhenUnset: true })
  const view = {
    hasTemplate: !!(d.waTemplateKey || d.templateId || structure.length),
    headerType: 'NONE',
    headerText: '',
    mediaLink: '',
    media: '',
    fileName: '',
    bodyText: '',
    footerText: '',
    urlButtons: [],
  }
  for (let i = 0; i < components.length; i++) {
    const comp = components[i]
    if (!comp) continue
    const type = upper(comp.type)
    if (type === 'HEADER') {
      const fmt = upper(comp.format)
      if (fmt === 'TEXT') {
        view.headerType = 'TEXT'
        const ex = (comp.example && comp.example.header_text) || []
        view.headerText = replaceVars(comp.text, ex)
      } else if (fmt === 'IMAGE' || fmt === 'VIDEO' || fmt === 'DOCUMENT') {
        view.headerType = fmt
        const handles = (comp.example && comp.example.header_handle) || []
        view.mediaLink = handles[0] ? String(handles[0]) : ''
        view.fileName = comp.fileName || ''
        for (let s = 0; s < slotList.length; s++) {
          const slot = slotList[s]
          if (!slot || upper(slot.type) !== 'HEADER') continue
          const sf = lower(slot.format)
          if (sf !== 'image' && sf !== 'video' && sf !== 'document') continue
          view.media = slot.media || view.media
          if (!view.mediaLink) view.mediaLink = slot.mediaLink || slot.media || ''
          if (!view.fileName) view.fileName = slot.fileName || ''
          break
        }
      }
    } else if (type === 'BODY') {
      const bodyEx = (comp.example && comp.example.body_text && comp.example.body_text[0]) || []
      view.bodyText = replaceVars(comp.text, bodyEx)
    } else if (type === 'FOOTER') {
      view.footerText = comp.text || ''
    } else if (type === 'BUTTONS') {
      const buttons = Array.isArray(comp.buttons) ? comp.buttons : []
      for (let b = 0; b < buttons.length; b++) {
        const btn = buttons[b]
        const t = upper(btn && btn.type)
        if (t === 'URL' || t === 'PHONE' || t === 'PHONE_NUMBER') {
          view.urlButtons.push({ type: t, text: (btn && btn.text) || '' })
        }
      }
    }
  }
  return view
}
